import { IcRoutes, IcWhatsapp } from "@racoon/icons";

import { Button, Hyperlink, fmt, Separator } from "@racoon/ui";
import { useEffect, useMemo, useState } from "react";
import { Row } from "./row";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { formatPhoneNumber, reformatPhoneNumber, fromTimestamp, sortJobsByStep } from "@racoon/lib";
import { useRouteJobsModal } from "~/pages/dashboard/routes/hooks/useRouteJobsModal";
import {
  useGetRouteQuery,
  useForceCompleteRouteMutation,
  useCancelRouteMutation,
} from "~/shared/api/routes/route.grpc";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConnectProto, RouteStatus, JobEvent } from "@racoon/api";
import { ForceCompleteModal } from "~/pages/dashboard/routes/components/ForceCompleteModal";
import { RouteStatusBadge } from "~/pages/dashboard/routes/components/RouteStatusBadge";
import { badge } from "~/shared/helpers";
import { useNavigate } from "react-router-dom";
import { useInfoPanel } from "../model/useInfoPanel";
import { EtaModal } from "./eta-modal";
import { ChevronRight } from "lucide-react";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useUserCard } from "~/pages/dashboard/users/ui/use-user-card";

export function RouteInfoCard({
  id,
  setHeader,
}: {
  id: string;
  setHeader?: (element: React.ReactNode) => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const activeCompanyId = useAuthSession().activeCompanyId;
  const { open: openInfoPanel } = useInfoPanel();
  const { openUser, userCard } = useUserCard();
  const { data: routeData } = useGetRouteQuery(
    id && activeCompanyId
      ? { id: BigInt(id), ownerOrganizationId: BigInt(activeCompanyId) }
      : skipToken,
  );

  // ETA calculation function
  const calculateETA = (date: Date | null | undefined, status: ConnectProto.RouteStatus) => {
    if (!date) {
      return t(Namespace.Infopanel, "empty");
    }

    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes <= 0 || status === ConnectProto.RouteStatus.COMPLETED) {
      return t(Namespace.Infopanel, "eta.arrived");
    }

    return t(Namespace.Infopanel, "eta.minutesLeft", { minutes });
  };

  // Function to break down ETA per job
  const getEtaBreakdown = () => {
    if (!data || !data.jobs?.length) {
      return { jobs: [], total_time: "-" };
    }

    const route_start = fromTimestamp(data.planStartAt);
    const route_end = fromTimestamp(data.planEndAt);

    if (!route_start || !route_end) {
      return { jobs: [], total_time: "-" };
    }

    const sortedJobs = sortJobsByStep(data.jobs.filter((job) => job.planStartAt && job.planEndAt));

    if (sortedJobs.length === 0) {
      return { jobs: [], total_time: "-" };
    }

    const total_time = Math.floor((route_end.getTime() - route_start.getTime()) / 60000);

    const jobsBreakdown = sortedJobs.map((job, index) => {
      const job_start = fromTimestamp(job.factStartAt) ?? fromTimestamp(job.planStartAt)!;
      const job_end = fromTimestamp(job.factEndAt) ?? fromTimestamp(job.planEndAt)!;

      const service_time = Math.floor((job_end.getTime() - job_start.getTime()) / 60000);

      let depot_to_job = 0;
      if (index === 0) {
        depot_to_job = Math.floor((job_start.getTime() - route_start.getTime()) / 60000);
      } else {
        const prev_job = sortedJobs[index - 1];
        const prev_job_end =
          fromTimestamp(prev_job.factEndAt) ?? fromTimestamp(prev_job.planEndAt)!;
        depot_to_job = Math.floor((job_start.getTime() - prev_job_end.getTime()) / 60000);
      }

      let job_to_depot = 0;
      if (index === sortedJobs.length - 1) {
        job_to_depot = Math.floor((route_end.getTime() - job_end.getTime()) / 60000);
      } else {
        const next_job = sortedJobs[index + 1];
        const next_job_start =
          fromTimestamp(next_job.factStartAt) ?? fromTimestamp(next_job.planStartAt)!;
        job_to_depot = Math.floor((next_job_start.getTime() - job_end.getTime()) / 60000);
      }

      return {
        id: Number(job.id),
        depot_to_job: `${depot_to_job} мин`,
        service_time: `${service_time} мин`,
        job_to_depot: `${job_to_depot} мин`,
        status: job.status,
        is_completed: !!job.factEndAt,
        is_first: index === 0,
        is_last: index === sortedJobs.length - 1,
        original_job: job,
      };
    });

    return {
      jobs: jobsBreakdown,
      total_time: `${total_time} мин`,
    };
  };

  const data = routeData;

  // MSW (ТБО) routes carry dozens of jobs, so listing them all is useless here —
  // only the job the driver is currently on is shown.
  const isMswRoute = data?.routeCategory === ConnectProto.RouteCategory.MSW;

  // The job in progress: on-service wins over en-route (same rule as the gantt labels
  // in ~/components/routes.tsx). With nothing in progress fall back to the next pending
  // step, and on a finished route to the last one, so the section is never empty.
  const activeJob = useMemo(() => {
    const jobs = data?.jobs ?? [];
    if (jobs.length === 0) return undefined;

    const byStep = sortJobsByStep(jobs);

    return (
      byStep.find((job) => job.status === ConnectProto.JobStatus.ON_SERVICE) ??
      byStep.find((job) => job.status === ConnectProto.JobStatus.ENROUTE) ??
      byStep.find(
        (job) =>
          job.status !== ConnectProto.JobStatus.COMPLETED &&
          job.status !== ConnectProto.JobStatus.FORCE_COMPLETED &&
          job.status !== ConnectProto.JobStatus.CANCELLED,
      ) ??
      byStep[byStep.length - 1]
    );
  }, [data]);

  // Jobs rendered in the orders section below — the active one alone for ТБО routes.
  const visibleJobs = isMswRoute ? (activeJob ? [activeJob] : []) : (data?.jobs ?? []);

  // Set title when data is loaded
  useEffect(() => {
    if (data && setHeader) {
      const element = (
        <div className="flex items-center gap-2">
          <IcRoutes width={24} height={24} className="text-brand-blue" />
          <span className="font-semibold text-lg">
            {t(Namespace.Infopanel, "labels.route")} M{Number(data?.id)}
          </span>
        </div>
      );
      setHeader(element);
    }
  }, [data, setHeader]);

  // ---- Modification here: expanded and hook for expanding first job by default ----
  const [expanded, setExpanded] = useState<string | null>(null);

  // Expand the first rendered job by default when data is loaded or changed
  useEffect(() => {
    if (visibleJobs.length > 0) {
      setExpanded(visibleJobs[0].id?.toString());
    }
  }, [data]);
  // ----------------------------------------------------------------------------------

  const [showEtaModal, setShowEtaModal] = useState(false);
  const { openRouteJobs } = useRouteJobsModal();

  // Mutations for cancelling and completing the route
  const [forceCompleteRouteMutation, { isLoading: isCompleting }] = useForceCompleteRouteMutation();
  const [forceCancelRouteMutation, { isLoading: isCancelling }] = useCancelRouteMutation();

  // Determine which buttons to show based on status
  const canForceComplete =
    data?.status === ConnectProto.RouteStatus.IN_PROGRESS ||
    data?.status === ConnectProto.RouteStatus.RETURNING;
  const canForceCancel =
    data?.status === ConnectProto.RouteStatus.CREATED ||
    data?.status === ConnectProto.RouteStatus.IN_PROGRESS;

  // Force complete/cancel modal state
  const [isForceCompleteModalOpen, setIsForceCompleteModalOpen] = useState(false);
  const [isForceCancelModalOpen, setIsForceCancelModalOpen] = useState(false);

  // Jobs that still need a resolution before completing/cancelling the route
  const unfinishedJobs =
    data?.jobs.filter(
      (job) =>
        job.status !== ConnectProto.JobStatus.COMPLETED &&
        job.status !== ConnectProto.JobStatus.CANCELLED,
    ) ?? [];

  // Button handlers — open the resolution modal when there are unfinished jobs,
  // otherwise fire the mutation directly.
  const handleForceCompleteRoute = () => {
    if (unfinishedJobs.length > 0) setIsForceCompleteModalOpen(true);
    else handleConfirmForceComplete({});
  };

  const handleConfirmForceComplete = async (resolutions: Record<number, JobEvent>) => {
    if (!activeCompanyId) return;
    try {
      const jobsToComplete: bigint[] = [];
      const jobsToUnassign: bigint[] = [];
      Object.entries(resolutions).forEach(([jobIdStr, event]) => {
        const jobId = BigInt(jobIdStr);
        if (event === JobEvent.JOB_EVENT_FORCE_COMPLETE) {
          jobsToComplete.push(jobId);
        } else {
          jobsToUnassign.push(jobId);
        }
      });

      await forceCompleteRouteMutation({
        id: BigInt(id),
        ownerOrganizationId: BigInt(activeCompanyId),
        jobsToComplete,
        jobsToUnassign,
      }).unwrap();
      setIsForceCompleteModalOpen(false);
    } catch (error) {
      console.error("Error force completing route:", error);
    }
  };

  const handleForceCancelRoute = () => {
    if (unfinishedJobs.length > 0) setIsForceCancelModalOpen(true);
    else handleConfirmForceCancel({});
  };

  const handleConfirmForceCancel = async (resolutions: Record<number, JobEvent>) => {
    if (!activeCompanyId) return;
    try {
      const jobsToCancel: bigint[] = [];
      const jobsToUnassign: bigint[] = [];
      Object.entries(resolutions).forEach(([jobIdStr, event]) => {
        const jobId = BigInt(jobIdStr);
        if (event === JobEvent.JOB_EVENT_CANCEL) {
          jobsToCancel.push(jobId);
        } else {
          jobsToUnassign.push(jobId);
        }
      });

      await forceCancelRouteMutation({
        id: BigInt(id),
        ownerOrganizationId: BigInt(activeCompanyId),
        jobsToCancel,
        jobsToUnassign,
      }).unwrap();
      setIsForceCancelModalOpen(false);
    } catch (error) {
      console.error("Error force cancelling route:", error);
    }
  };

  // Click handler to navigate to jobs table
  const handleOrderClick = (orderId: string | number) => {
    if (activeCompanyId) {
      const url = `/${activeCompanyId}/orders?highlightOrder=${orderId}`;
      navigate(url);
    }
  };

  // Click handler to open pickup location card
  const handleLocationClick = (locationId: string | number) => {
    openInfoPanel("pickup", String(locationId));
  };

  // Click handler to open truck card
  const handleTruckClick = (truckId: string | number) => {
    openInfoPanel("truck", String(truckId));
  };

  return (
    <div className="p-4  h-full flex flex-col">
      <div className="flex-1 space-y-4">
        <div className="space-y-[14px]">
          <Row label={t(Namespace.Infopanel, "labels.taskType")}>
            <span className="float-right text-black">Вывоз мусора</span>
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.status")}>
            <RouteStatusBadge
              status={
                `${ConnectProto.RouteStatusSchema.values[data?.status ?? 0].name}` as RouteStatus
              }
              jobs={data?.jobs as any}
              completedJobs={
                data?.jobs.filter((job) => job.status === ConnectProto.JobStatus.COMPLETED).length
              }
            />
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.truck")}>
            <Hyperlink
              className="float-right"
              title="Перейти к грузовику"
              onOpen={() => {
                if (data?.driverOnShift?.truck?.id) {
                  handleTruckClick(String(data.driverOnShift.truck.id));
                }
              }}
            >
              {data?.driverOnShift?.truck?.plateNumber || "—"}
            </Hyperlink>
          </Row>
          <Row label={t(Namespace.Infopanel, isMswRoute ? "labels.activeOrder" : "labels.order")}>
            {isMswRoute ? (
              activeJob ? (
                <Hyperlink
                  className="float-right"
                  title="Перейти к заказу"
                  onOpen={() => handleOrderClick(Number(activeJob.id))}
                >
                  {String(activeJob.id)}
                </Hyperlink>
              ) : (
                <span className="float-right">—</span>
              )
            ) : (
              data?.jobs.map((order) => (
                <Hyperlink
                  key={String(order.id)}
                  className="ml-2"
                  title="Перейти к заказу"
                  onOpen={() => handleOrderClick(Number(order.id))}
                >
                  {String(order.id)}
                </Hyperlink>
              ))
            )}
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.driver")}>
            <span className="float-right">
              {data?.driverOnShift?.driver ? (
                <Hyperlink onOpen={() => openUser(data.driverOnShift!.driver!)}>
                  {data.driverOnShift.driver.firstName} {data.driverOnShift.driver.lastName}
                </Hyperlink>
              ) : (
                t(Namespace.Infopanel, "empty")
              )}
            </span>
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.phone")}>
            <span className="float-right flex items-center gap-1 flex-1">
              <IcWhatsapp />
              <a
                href={`https://wa.me/${reformatPhoneNumber(data?.driverOnShift?.driver?.phoneNumber?.replace(/\s+/g, "") ?? "")}`}
                target="_blank"
                rel="noreferrer"
              >
                <span className="underline">
                  {formatPhoneNumber(data?.driverOnShift?.driver?.phoneNumber ?? "")}
                </span>
              </a>
            </span>
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.startTime")}>
            <span className="float-right">
              {data?.factStartAt ? fmt.dateTime(fromTimestamp(data.factStartAt) as Date) : "-"}
            </span>
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.etaEnd")}>
            <span
              className="float-right cursor-pointer text-blue-600 hover:underline"
              onClick={() => setShowEtaModal(true)}
            >
              {calculateETA(
                fromTimestamp(data?.planEndAt) ?? null,
                data?.status ?? ConnectProto.RouteStatus.UNDEFINED,
              )}
            </span>
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.distance")}>
            <span className="float-right">—</span>
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.movementHistory")}>
            <span
              className="float-right underline text-blue-700 cursor-pointer hover:text-blue-900"
              onClick={() => {
                if (activeCompanyId && data && data.driverOnShift) {
                  const startTime = fromTimestamp(data.factStartAt ?? data.planStartAt);
                  const endTime = fromTimestamp(data.factEndAt ?? data.planEndAt);
                  const shiftId = data.driverOnShift.id;
                  const truckId = data.driverOnShift.truckId;

                  let startDateParam = "";
                  let endDateParam = "";

                  if (startTime) {
                    const startDate = new Date(startTime);
                    startDate.setHours(0, 0, 0, 0);
                    startDateParam = `&start=${startDate.toISOString()}`;
                  }

                  if (endTime) {
                    const endDate = new Date(endTime);
                    endDate.setHours(23, 59, 59, 999);
                    endDateParam = `&end=${endDate.toISOString()}`;
                  }

                  const url = `/${activeCompanyId}/gps-tracking?shiftId=${shiftId}&truckId=${truckId}${startTime ? `&startTime=${startTime.getTime()}` : ""}${startDateParam}${endDateParam}`;
                  navigate(url);
                }
              }}
              title="Перейти к истории движения"
            >
              {t(Namespace.Infopanel, "actions.viewHistory")}
            </span>
          </Row>
        </div>
        <Separator />
        {/* Orders Section */}
        <div className="space-y-2">
          {visibleJobs.map((order) => (
            <div key={String(order.id)} className="bg-[#F2F4F7] rounded-lg  p-4 mb-2">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpanded(expanded === String(order.id) ? null : String(order.id))}
              >
                <div
                  className="
                    flex items-center gap-1 
                    font-inter font-normal 
                    text-[14px] leading-[14px] 
                    not-italic align-bottom 
                    tracking-[-0.01em]
                  "
                  style={{
                    fontFamily: "Inter",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "14px",
                    lineHeight: "14px",
                    letterSpacing: "-0.01em",
                    verticalAlign: "bottom",
                  }}
                >
                  <div
                    className={`${
                      expanded === String(order.id) ? "rotate-90 !text-brand-blue" : ""
                    }`}
                  >
                    <ChevronRight width={16} height={16} />
                  </div>
                  Z{String(order.id)}
                </div>
                <div className="inline-flex items-center gap-2">
                  {badge(
                    t(
                      Namespace.Enum,
                      ConnectProto.JobStatusSchema.values[order.status].name as TranslationKey,
                    ),
                    ConnectProto.JobStatusSchema.values[order.status].name as any,
                  )}
                </div>
              </div>
              {expanded === String(order.id) && (
                <div className="mt-2 text-sm text-slate-600 space-y-[12px]">
                  <Row label={t(Namespace.Infopanel, "labels.object")}>
                    <Hyperlink
                      title="Открыть карточку локации"
                      onOpen={() => {
                        if (order.pickupLocationId) {
                          handleLocationClick(Number(order.pickupLocationId));
                        }
                      }}
                    >
                      {order.pickupLocation?.name ?? "-"}
                    </Hyperlink>
                  </Row>
                  <Row label={t(Namespace.Infopanel, "labels.phone")}>
                    <span className="inline-flex items-center gap-1">
                      <IcWhatsapp />
                      <a href={`https://wa.me/`} target="_blank" rel="noreferrer">
                        <span className=" text-text-primary">—</span>
                      </a>
                    </span>
                  </Row>
                  <Row label={t(Namespace.Infopanel, "labels.eta")}>
                    <span className="float-right">—</span>
                  </Row>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-auto space-y-2 pt-4">
        <div className="flex gap-2">
          <Button
            id="route-info-card-button"
            variant="opacityBlueWithoutOutline"
            className="flex-1 bg-blue-100 text-blue-700"
            onClick={() => {
              if (data) openRouteJobs(data);
            }}
          >
            {t(Namespace.Infopanel, "actions.cardRoute")}
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            id="route-info-cancel-button"
            className="flex-1 "
            variant="opacityRedWithoutOutline"
            onClick={handleForceCancelRoute}
            disabled={!canForceCancel || isCancelling}
          >
            {isCancelling
              ? t(Namespace.Infopanel, "actions.cancelling")
              : t(Namespace.Buttons, "cancel")}
          </Button>
          <Button
            id="route-info-complete-button"
            className="flex-1"
            variant="opacityGreenWithoutOutline"
            onClick={handleForceCompleteRoute}
            disabled={!canForceComplete || isCompleting}
          >
            {isCompleting
              ? t(Namespace.Infopanel, "actions.completing")
              : t(Namespace.Buttons, "completeRoute")}
          </Button>
        </div>
      </div>

      {/* ETA Modal */}
      <EtaModal
        open={showEtaModal}
        onOpenChange={setShowEtaModal}
        data={data as any}
        getEtaBreakdown={getEtaBreakdown}
      />

      {/* Force complete / cancel resolution modals */}
      {isForceCompleteModalOpen && (
        <ForceCompleteModal
          open={isForceCompleteModalOpen}
          onOpenChange={setIsForceCompleteModalOpen}
          unfinishedJobs={unfinishedJobs}
          onConfirm={handleConfirmForceComplete}
          isLoading={isCompleting}
        />
      )}
      {isForceCancelModalOpen && (
        <ForceCompleteModal
          open={isForceCancelModalOpen}
          onOpenChange={setIsForceCancelModalOpen}
          mode="cancel"
          unfinishedJobs={unfinishedJobs}
          onConfirm={handleConfirmForceCancel}
          isLoading={isCancelling}
        />
      )}
      {userCard}
    </div>
  );
}
